import React, { Fragment } from "react";
import PropTypes from "prop-types";
import {
  useExpanded,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import _ from "lodash";

import { INPUT_WHITESPACES_REGEX } from "../input/input.helpers";
import { HeaderContextProvider, TableCaption } from "./components";
import { THead } from "./components/THead";
import { TBody } from "./components/TBody";
import { TFoot } from "./components/TFoot";
import { Pagination } from "./components/Pagination";
import { alphanumericSort, containsSubString, searchInArray } from "./helpers";
import { isMobile } from "../../helpers/dom.helper";
import { TableSortStatus } from "./components";

import "./Table.scss";

export const Table = ({
  columns,
  data,
  header,
  defaultSorted,
  noDataMessageID,
  noFilteredMessageID,
  renderGlobalFooter,
  renderRowSubComponent,
  searchable,
  searchKeys,
  showPagination,
  subHeader,
  hiddenColumns,
  visibleHeader,
  searchAriaLabelMessage,
  caption,
  tableName,
  isLoading,
  dataFailure,
  onRefreshClick,
}) => {
  const globalFilter = (rows, columnIds, globalFilterValue) => {
    return rows.filter(({ original: row }) => {
      const rowValues = _.pick(row, searchKeys);
      const formattedFilterValue = globalFilterValue
        .replace(INPUT_WHITESPACES_REGEX, " ")
        .trim();

      return _.values(rowValues).some((value) =>
        _.isArray(value)
          ? searchInArray(value, formattedFilterValue)
          : containsSubString(value, formattedFilterValue)
      );
    });
  };

  const tableProps = useTable(
    {
      columns,
      data,
      initialState: {
        pageSize: 25,
        sortBy: defaultSorted,
        hiddenColumns,
      },
      sortTypes: { alphanumeric: alphanumericSort }, // override the default "react-table" sorting
      autoResetSortBy: false,
      ...(searchable && { globalFilter }),
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination
  );

  const {
    allColumns,
    getTableProps,
    headers,
    toggleSortBy,
    setGlobalFilter,
    state,
    visibleColumns,
    rows,
    headerGroups,
  } = tableProps;

  const isPaginationVisible = isMobile()
    ? showPagination && rows.length > 25
    : showPagination;

  return (
    <Fragment>
      <HeaderContextProvider
        state={state}
        setGlobalFilter={setGlobalFilter}
        allColumns={allColumns}
        toggleSortBy={toggleSortBy}
        ariaLabelMessage={searchAriaLabelMessage}
      >
        {header}
      </HeaderContextProvider>
      <div className={"table"}>
        <table className={"rt-table"} {..._.omit(getTableProps(), "role")}>
          <TableCaption caption={caption} />
          <THead
            headers={headers}
            subHeader={subHeader}
            visibleHeader={visibleHeader}
            visibleColumns={visibleColumns}
          />
          <TBody
            noDataMessageID={noDataMessageID}
            noFilteredMessageID={noFilteredMessageID}
            renderRowSubComponent={renderRowSubComponent}
            showPagination={showPagination}
            tableProps={tableProps}
            isLoading={isLoading}
            isLoadFailed={dataFailure}
            onRefreshClick={onRefreshClick}
          />
          {renderGlobalFooter && (
            <TFoot
              renderGlobalFooter={renderGlobalFooter}
              tableProps={tableProps}
            />
          )}
        </table>
        {isPaginationVisible && <Pagination {...tableProps} />}
      </div>
      {tableName && (
        <TableSortStatus tableName={tableName} headerGroups={headerGroups} />
      )}
    </Fragment>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  header: PropTypes.node,
  defaultSorted: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, desc: PropTypes.bool })
  ),
  noDataMessageID: PropTypes.string,
  noFilteredMessageID: PropTypes.string,
  renderGlobalFooter: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  renderRowSubComponent: PropTypes.func,
  searchable: PropTypes.bool,
  searchKeys: PropTypes.arrayOf(PropTypes.string),
  showPagination: PropTypes.bool,
  subHeader: PropTypes.node,
  hiddenColumns: PropTypes.arrayOf(PropTypes.string),
  visibleHeader: PropTypes.bool,
  searchAriaLabelMessage: PropTypes.string,
  caption: PropTypes.string,
  tableName: PropTypes.string,
  isLoading: PropTypes.bool,
  dataFailure: PropTypes.bool,
  onRefreshClick: PropTypes.func,
};

Table.defaultProps = {
  defaultSorted: [],
  hiddenColumns: [],
  noDataMessageID: "no_available_data",
  noFilteredMessageID: "no_matches_found",
  searchable: true,
  visibleHeader: true,
  isLoading: false,
  dataFailure: false,
};
